import React from 'react';
import getThemeClass from '../utils/getThemeClass';
import styles from './Cutouts.module.css';

/**
 * @param {Array<FF.Cutout>=} cutouts
 */
function validateCutouts(cutouts = []) {
  let isHorizontal = false;
  let isVertical = false;

  cutouts.forEach(cutout => {
    if (['Top', 'Bottom'].includes(cutout.orientation)) {
      isVertical = true;
    }
    if (['Left', 'Right'].includes(cutout.orientation)) {
      isHorizontal = true;
    }
  });

  /*
   * this check is done because we have not solved how the cutouts intersect in the corner
   * TODO: see if there's a way in CSS to crop the mask-image at a 45 degree angle
   */
  if (isHorizontal && isVertical) {
    throw new Error(
      `Cutouts with contentful ids: ${cutouts
        .map(cutout => cutout.contentful_id)
        .join(
          ', ',
        )} are used together, but are both horizontal and vertical orientations.
        cutouts must be ONLY horizontal (Left/Right) or vertical (Top/Bottom)`,
    );
  }
}

/**
 * @param {Array<FF.Cutout>} cutouts
 * @param {Boolean=} noPadding
 */
function getWrapperClassName(cutouts, noPadding) {
  if (noPadding) {
    return '';
  }

  let classKey = 'wrapperPadding';

  const classMap = new Map();
  cutouts.forEach(({ orientation }) => classMap.set(orientation, true));

  ['Top', 'Bottom', 'Left', 'Right'].forEach(orientationType => {
    if (classMap.get(orientationType)) {
      classKey += orientationType;
    }
  });

  return styles[classKey];
}

/**
 * @param {object} props
 * @param {String=} props.className
 * @param {Array<FF.Cutout>|null=} props.cutouts
 * @param {React.ReactNode=} props.children
 * @param {Boolean=} props.noPadding
 */
export default function Cutouts({
  className = '',
  cutouts,
  children,
  noPadding = false,
}) {
  // if Cutouts is null
  if (!Array.isArray(cutouts)) {
    return <>{children}</>;
  }

  validateCutouts(cutouts);

  if (!React.isValidElement(children)) {
    throw new Error('Cannot use Cutouts without valid children prop');
  }
  if (Array.isArray(children)) {
    throw new Error('Cannot use Cutouts with multiple children');
  }
  return (
    <>
      {/**
       *
       * Cutouts needs a way to:
       * - add padding & styles to the existing wrapper around the content
       * - place the actual "cutout" element as a child of that wrapper, as sibliggs to the content
       *
       * This is counterintuitive, since <Cutouts> wraps around the content but ends up as a "child",
       * but its a clean way to keep all the work in this component
       *
       * See:
       * - react docs: https://reactjs.org/docs/react-api.html#cloneelement
       * - tutorial: https://atomizedobjects.com/blog/quick-fixes/how-to-pass-props-to-children-in-react/
       */}
      {React.cloneElement(children, {
        className: `${children.props.className || ''} ${getWrapperClassName(
          cutouts,
          noPadding,
        )}`,
        children: (Array.isArray(children.props.children)
          ? children.props.children
          : [children.props.children]
        ).concat(
          cutouts.map(({ color, shape, orientation }) => (
            <div
              key={`${color}-${shape}-${orientation}`}
              className={`identifier--cutouts ${getThemeClass(color)} ${
                styles[shape.replaceAll(' ', '')]
              } ${styles[orientation]} ${className}`}
            />
          )),
        ),
      })}
    </>
  );
}
